import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import { User } from '~/types/User';
import { BootMark, markBootOnce } from '~/utils/bootTiming';

interface Props {
  enableReady: boolean;
  user?: User;
}

export function useFlagsReady({ enableReady, user }: Props) {
  const [isReady, setIsReady] = useState(enableReady);
  const ldClient = useLDClient();

  // Depend on the three fields `userToLdUser` actually reads rather than on the
  // `user` object itself. `user` comes from redux and its reference changes as
  // auth data lands (cached profile first, then the revalidated one), which used
  // to re-run `identify()` on every change — three serialized round trips to
  // LaunchDarkly during startup for an identical context.
  const clientId = user?.client;
  const clientName = user?.client_name;
  const subDomain = user?.client_config?.sub_domain;

  useEffect(() => {
    if (!ldClient || clientId === undefined) return;

    const key = clientId.toString();
    ldClient
      .identify({
        key,
        name: clientName,
        custom: {
          client_id: key,
          ...(subDomain !== undefined && { sub_domain: subDomain }),
        },
      })
      .then(() => {
        markBootOnce(BootMark.FirstLdIdentifyEnd);
        setIsReady(true);
      });
  }, [ldClient, clientId, clientName, subDomain]);

  return isReady;
}
